import React from 'react';
import { Award, BookOpen, Users, Star, GraduationCap, CheckCircle } from 'lucide-react';

const AboutSection = () => {
  const universities = [
    {
      name: 'University of Oxford',
      qualification: 'PGCE Education',
      logo: 'https://static.prod-images.emergentagent.com/jobs/30b73cfb-2a7e-4a88-a15f-a7e3cd8fbd24/images/7141adce85e1e993017e58a9f1af3fe468ccace77521b0bb0cd49be7e37ef4b1.jpeg',
      color: '#0F172A'
    },
    {
      name: 'University of Birmingham',
      qualification: 'BSc & MSc',
      logo: 'https://static.prod-images.emergentagent.com/jobs/b923c6b5-9fc7-454d-aa66-1af482eca6d9/images/1f74662ca5dc7cfdd601b2eae1583396d82d11cbbc89c6e28aa0ed0266c7eec9.png',
      color: '#C5A059'
    },
    {
      name: 'Swansea University',
      qualification: 'PhD Medical Sciences',
      logo: 'https://static.prod-images.emergentagent.com/jobs/b923c6b5-9fc7-454d-aa66-1af482eca6d9/images/ce6566833a4c7456e8452687f2186ca42c01e2487dbc65bc27f0c6712b185d64.png',
      color: '#0F172A'
    }
  ];

  const credentials = [
    "Oxford PGCE Qualified Teacher",
    "Active IGCSE Examiner",
    "12+ Years Teaching Experience",
    "PhD in Medical Sciences",
    "SEN Specialist",
    "DBS Checked"
  ];

  return (
    <section id="about" style={{ padding: '5rem 1.5rem', background: 'var(--bg-section)' }}>
      <div className="container">
        {/* Main Content - Photo and Bio Side by Side */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '3rem',
          alignItems: 'center',
          marginBottom: '3rem'
        }}>
          {/* Left - Photo */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              position: 'relative',
              display: 'inline-block'
            }}>
              <img
                src="https://customer-assets.emergentagent.com/job_tutor-abba/artifacts/1zxnvv6b_Screenshot_20260114_125045_Chrome.jpg"
                alt="Dr. Michael Omakobia - ABBA Academy"
                style={{
                  width: '100%',
                  maxWidth: '350px',
                  borderRadius: '20px',
                  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15)',
                  border: '4px solid white'
                }}
              />
              {/* Badge */}
              <div style={{
                position: 'absolute',
                bottom: '-15px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
                color: 'white',
                padding: '0.5rem 1.5rem',
                borderRadius: '9999px',
                fontWeight: 700,
                fontSize: '0.9rem',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 15px rgba(13, 148, 136, 0.4)'
              }}>
                🎓 Oxford-Trained Educator
              </div>
            </div>
          </div>

          {/* Right - Bio */}
          <div>
            <h2 style={{ 
              fontSize: 'clamp(1.75rem, 4vw, 2.25rem)', 
              fontWeight: 800, 
              color: 'var(--text-primary)',
              marginBottom: '1rem'
            }}>
              Meet Dr. Michael Omakobia
            </h2>
            <p style={{ 
              fontSize: '1.1rem', 
              color: 'var(--text-secondary)', 
              lineHeight: 1.7,
              marginBottom: '1.5rem'
            }}>
              With over <strong style={{ color: 'var(--accent-text)' }}>12 years of teaching experience</strong> in secondary schools and private tuition, I've dedicated my career to helping students not just pass their exams, but truly understand and love Maths.
            </p>
            <p style={{ 
              fontSize: '1.1rem', 
              color: 'var(--text-secondary)', 
              lineHeight: 1.7,
              marginBottom: '1.5rem'
            }}>
              As an <strong style={{ color: 'var(--accent-text)' }}>active IGCSE examiner</strong>, I know exactly what examiners are looking for – and I share these insider tips with every student I teach.
            </p>

            {/* Credentials Checklist */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.75rem'
            }}>
              {credentials.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CheckCircle size={18} color="#C5A059" />
                  <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* University Credentials */}
        <div style={{
          maxWidth: '900px',
          margin: '0 auto 3rem',
          padding: '1.5rem 2rem',
          background: 'linear-gradient(135deg, rgba(13, 148, 136, 0.08) 0%, rgba(20, 184, 166, 0.05) 100%)',
          borderRadius: '16px',
          border: '2px solid var(--accent-primary)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '1rem'
          }}>
            <GraduationCap size={24} color="var(--accent-text)" />
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: 700, 
              color: 'var(--accent-text)',
              margin: 0
            }}>
              Academic Credentials
            </h3>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '3rem',
            flexWrap: 'wrap'
          }}>
            {universities.map((uni, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <div style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  padding: '10px'
                }}>
                  <img 
                    src={uni.logo} 
                    alt={uni.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 700, 
                    color: 'var(--text-primary)'
                  }}>
                    {uni.name}
                  </div>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: 'var(--text-secondary)'
                  }}>
                    {uni.qualification}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Photos Gallery */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <div style={{
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
          }}>
            <img
              src="https://customer-assets.emergentagent.com/job_tutor-abba/artifacts/e7mk0l04_20260114_122752.jpg"
              alt="Dr. Michael Omakobia - ABBA Academy"
              style={{
                width: '100%',
                height: '280px',
                objectFit: 'cover'
              }}
            />
          </div>
          <div style={{
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
          }}>
            <img
              src="https://customer-assets.emergentagent.com/job_tutor-abba/artifacts/bczfir6e_20260114_122315%280%29.jpg"
              alt="Dr. Michael Omakobia"
              style={{
                width: '100%',
                height: '280px',
                objectFit: 'cover'
              }}
            />
          </div>
          <div style={{
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
          }}>
            <img
              src="https://customer-assets.emergentagent.com/job_tutor-abba/artifacts/3rm3svm4_20260114_121745.jpg"
              alt="Dr. Michael Omakobia"
              style={{
                width: '100%',
                height: '280px',
                objectFit: 'cover'
              }}
            />
          </div>
        </div>

        {/* Quote */}
        <div style={{
          maxWidth: '700px',
          margin: '3rem auto 0',
          textAlign: 'center',
          padding: '2rem',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          borderLeft: '4px solid var(--accent-primary)'
        }}>
          <p style={{
            fontSize: '1.2rem',
            fontStyle: 'italic',
            color: 'var(--text-primary)',
            lineHeight: 1.6,
            marginBottom: '1rem'
          }}>
            "Every student has the potential to excel in Maths. My job is to unlock that potential and show them what they're truly capable of."
          </p>
          <p style={{
            fontWeight: 700,
            color: 'var(--accent-text)',
            margin: 0
          }}>
            — Dr. Michael Omakobia
          </p>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
